import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import http from '../../services/http';
import useEstouMontado from '../../hooks/useEstouMontado';
import {
    INTERVALO_DO_BATIMENTO,
    avisoDePresenca,
    estouEditando,
    proximoCarimbo,
    temNovidade,
} from '../../utils/presencaNoChamado';

/**
 * Os dois avisos da tela do chamado aberto (Gisele, 23/10/2026).
 *
 * 1. "Identificar quando outro usuário estiver visualizando ou editando o
 *    mesmo ticket, evitando que duas pessoas façam alterações ao mesmo tempo
 *    sem perceber."
 * 2. "Caso outro usuário realize alguma alteração enquanto o ticket estiver
 *    aberto, apresentar um aviso informando que existem novas informações e
 *    disponibilizar a opção 'Atualizar Ticket'."
 *
 * ## Um batimento só para as duas perguntas
 *
 * Elas são feitas no mesmo instante, pela mesma tela, sobre o mesmo chamado --
 * `POST /ticket/{id}/presenca` devolve as duas respostas. Separá-las dobraria
 * o tráfego sem responder nada a mais.
 *
 * ## Por que um componente, e não mais 200 linhas no `TicketDetails`
 *
 * Aquele arquivo tem mais de 7.000 linhas e nenhum teste de componente. Aqui
 * dá para testar o batimento, o aviso e o botão -- e as decisões que erram em
 * silêncio (a redação, o momento, o que acontece quando a rede cai) moram em
 * `utils/presencaNoChamado`, com teste próprio.
 *
 * ## A falha de rede é SILENCIOSA de propósito
 *
 * Um batimento que não volta não vira mensagem de erro: a tela do chamado é
 * onde o operador trabalha o dia inteiro, e um alerta vermelho a cada oscilação
 * de rede treinaria todo mundo a ignorar os alertas desta tela -- inclusive os
 * dois que este componente existe para dar.
 */
const PresencaNoChamado = ({ ticketId, ultimaAlteracaoEm, carimboCarregado, onAtualizar }) => {
    const [outros, setOutros] = useState([]);
    const [novidade, setNovidade] = useState(false);

    // O batimento é um POST: quem fecha o chamado enquanto ele está no ar
    // voltava com "state update on an unmounted component" no console.
    const estouMontado = useEstouMontado();

    // O carimbo de referência vive num ref: ele muda dentro do batimento, e
    // como estado faria o efeito se reagendar a cada resposta.
    const carimboRef = useRef(carimboCarregado || null);

    // O mesmo para "estou editando": ele é lido NA HORA de bater, e não pode
    // reagendar o relógio a cada tecla digitada.
    const alteracaoRef = useRef(ultimaAlteracaoEm);
    useEffect(() => { alteracaoRef.current = ultimaAlteracaoEm; }, [ultimaAlteracaoEm]);

    /*
     * Quando o chamado é salvo (por mim ou pelo "Atualizar Ticket"), o pai
     * manda um carimbo novo -- e ele vira a referência. Sem isto, o meu
     * próprio salvamento apareceria como "há informações novas".
     */
    useEffect(() => {
        if (!carimboCarregado) return;

        carimboRef.current = carimboCarregado;
        setNovidade(false);
    }, [carimboCarregado]);

    const bater = useCallback(async () => {
        if (!ticketId || ticketId === 'new') return;

        try {
            const { data } = await http.post(`/ticket/${ticketId}/presenca`, {
                editando: estouEditando(alteracaoRef.current),
            });

            /*
             * A comparação vem ANTES de mexer no carimbo, e tem de continuar
             * vindo: `temNovidade` compara o carimbo GUARDADO com o que o
             * servidor acabou de mandar. Carimbar primeiro compararia o valor
             * com ele mesmo e o aviso de "informações novas" nunca apareceria.
             */
            const chegouNovidade = temNovidade(carimboRef.current, data?.atualizado_em);

            // O carimbo é ref, não estado: vale mesmo se a tela já saiu.
            carimboRef.current = proximoCarimbo(carimboRef.current, data?.atualizado_em);

            if (!estouMontado()) return;

            setOutros(Array.isArray(data?.outros) ? data.outros : []);

            if (chegouNovidade) {
                setNovidade(true);
            }
        } catch (e) {
            // Silêncio de propósito -- ver o cabeçalho.
        }
    }, [ticketId, estouMontado]);

    useEffect(() => {
        if (!ticketId || ticketId === 'new') return undefined;

        bater();
        const relogio = setInterval(bater, INTERVALO_DO_BATIMENTO);

        return () => {
            clearInterval(relogio);
            /*
             * Avisar que saí. Não é obrigatório (a validade de 60 s resolve
             * sozinha), mas sem isto quem fecha o chamado continua aparecendo
             * como presente por um minuto -- e o outro operador espera por
             * alguém que já saiu.
             *
             * `catch` vazio: a tela está sendo desmontada, não há a quem
             * avisar de uma falha aqui.
             */
            http.delete(`/ticket/${ticketId}/presenca`).catch(() => {});
        };
    }, [ticketId, bater]);

    const aviso = avisoDePresenca(outros);

    if (aviso.tipo === 'ninguem' && !novidade) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            {aviso.tipo !== 'ninguem' && (
                /*
                 * `warning` para quem edita, `info` para quem só lê. São
                 * consequências diferentes: ler junto não é problema, e dar o
                 * mesmo destaque aos dois ensinaria o operador a ignorar o
                 * aviso que importa.
                 */
                <Alert
                    severity={aviso.tipo === 'editando' ? 'warning' : 'info'}
                    data-testid={`presenca-${aviso.tipo}`}
                    sx={{ py: 0.25 }}
                >
                    {aviso.texto}
                </Alert>
            )}

            {novidade && (
                <Alert
                    severity="info"
                    data-testid="chamado-tem-novidade"
                    sx={{ py: 0.25 }}
                    action={(
                        <Button
                            color="inherit"
                            size="small"
                            data-testid="atualizar-ticket"
                            onClick={() => {
                                setNovidade(false);
                                if (onAtualizar) onAtualizar();
                            }}
                        >
                            Atualizar Ticket
                        </Button>
                    )}
                >
                    Há informações novas neste chamado.
                </Alert>
            )}
        </Box>
    );
};

export default PresencaNoChamado;
